import React from 'react';
import { get, map, find, cloneDeep, isEmpty, noop, split, trim, toString, parseInt } from 'lodash';
import { withRouter } from 'react-router-dom';
import queryString from 'query-string';
import moment from 'moment';

// local dependencies
import { memberInfoSettingPaymentInfo as MemberInfoSettingPaymentInfoView } from 'ui/views/widgets/memberInfoSettingPaymentInfo';
import { MemberInfoSettingPaymentInfoService } from 'services/memberInfoSettingPaymentInfo';
import * as confirmOverlay from 'utils/confirmOverlay';
import * as errorOverlay from 'utils/errorOverlay';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import { getLabel } from 'utils/getLabel';
import { SERVICE_RESPONSE_STATUS, SIZES } from 'constants/common';
import * as successOverlay from 'utils/successOverlay';
import { GLOBAL_LABELS } from 'constants/common';
import { overlay } from 'modules/overlay';
import { OVERLAY_VIEWS } from 'constants/templates';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { MemberInfoSettingPaymentInfo } from 'ui/containers/MemberInfoSettingPaymentInfo';
 *
 * // render
 * <MemberInfoSettingPaymentInfo propName='propValue'/>
 */
class _MemberInfoSettingPaymentInfo extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // bind
        this.handlePaymentDefaultSelection = this.handlePaymentDefaultSelection.bind( this );
        this.handleEditPaymentInfo = this.handleEditPaymentInfo.bind( this );
        this.handleInactivatePaymentInfo = this.handleInactivatePaymentInfo.bind( this );
        this.handleActivatePaymentInfo = this.handleActivatePaymentInfo.bind( this );
		this.submitActivatePaymentInfo = this.submitActivatePaymentInfo.bind( this );
        this.handleHsaFsaActionToggle = this.handleHsaFsaActionToggle.bind( this );

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
            showCustomErrorView: false
        };

        this.data = {
            memberId: get( this.props.requestPayload, 'context.memberId', get( this.props.location, 'state.memberId', undefined ) ),
            patientId: get( this.props.requestPayload, 'context.patientId', get( this.props.location, 'state.patientId', undefined ) )
        };

        // Member Detail instance
        this.memberInfoSettingPaymentInfoService = new MemberInfoSettingPaymentInfoService();

        // start member detail data fetch
        this.fetchComponentData();
    }

    /*************************************************/

    /**
     * @desc: fetch component data
     */
    async fetchComponentData() {
        log.debug( 'MemberInfoSettingPaymentInfo.fetchComponentData()' );

        // cancel ongoing request
        this.memberInfoSettingPaymentInfoService.cancel();

        // fetch component data
        const { status, data } = await this.memberInfoSettingPaymentInfoService.fetchComponentData( {
            url: this.props.widget.url,
            data: this.props.requestPayload.context
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            if( ! isEmpty( get( data, 'description', {} ) ) ) {
                this.setState( {
                    isFetching: false,
                    showCustomErrorView: true,
                    componentData: data

                    // networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
                } );
            } else {
                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
                } );
            }
        } else {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data
            } );
        }
    }

    /**
     * update default payment selection
     */
    async handlePaymentDefaultSelection( { card } ) {
        log.debug( 'MemberInfoSettingPaymentInfo.handlePaymentDefaultSelection()' );

        const memberId = cloneDeep( this.data.memberId );
        const patientId = cloneDeep( this.data.patientId );

        // cancel ongoing request
        this.memberInfoSettingPaymentInfoService.cancel();

        //show preloader
        pageLevelPreloader.show();

        const url = find( this.state.componentData.modifyActions, { name: 'setDefault' } )[ 'url' ];

        // set the address default
        const { status, data } = await this.memberInfoSettingPaymentInfoService.setDefaultPaymentInfo( {
            url,
            data: {
                context: {
                    memberId: memberId,
                    patientId: patientId
                },
                data: {
                    id: card.id
                }
            },
        } );

        //hide preloader
        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            // update address list
            const componentData = cloneDeep( this.state.componentData );
            const updatedList = map( componentData.paymentOptions, item => {
                item.flags.isDefault = card.id === item.id ? true: false;
                return item;
            } );
            componentData.paymentOptions = updatedList;
            this.setState( {
                componentData,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS
            }, () => {
                successOverlay.open( {
                    message: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_DONE_SUCCESSFULLY ),
                } );
            } );
            
        } else {
            if( ! isEmpty( get( data, 'description' ) ) ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
            } else {
                errorOverlay.open();
            }
        }
    }

    /**
     * update edit payment info
     */
    async handleEditPaymentInfo( { card, month, year } = {}, callback = noop ) {
        log.debug( 'MemberInfoSettingPaymentInfo.handleEditPaymentInfo()' );

        const action = find( this.state.componentData.modifyActions, { name: 'edit' } );

        const memberId = cloneDeep( this.data.memberId );
        const patientId = cloneDeep( this.data.patientId );
 
        // cancel ongoing request
        this.memberInfoSettingPaymentInfoService.cancel();

        //hide preloader
        pageLevelPreloader.show();

        // set the address default
        const { status, data } = await this.memberInfoSettingPaymentInfoService.submitEditedPaymentInfo( {
            url: action.url,
            data: {
                context: {
                    memberId: memberId,
                    patientId: patientId
                },
                data: {
                    id: card.id,
                    month: month,
                    year: year,
                }
            },
        } );

        //hide preloader
        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            // update expiry date
            const componentData = cloneDeep( this.state.componentData );
            const cardItem = find( componentData.paymentOptions, item => {
                return card.id === item.id;
            } );
            cardItem.expDate = `${ month }/${ year }`;

            this.setState( {
                componentData,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS
            }, callback );

        } else {

            if( ! isEmpty( get( data, 'description' ) ) ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
            } else {
                errorOverlay.open();
            }
        }
        
    }

    /**
	 * initiate activate payment info
	 */
	async handleActivatePaymentInfo( { card } ) {
		log.debug( 'MemberInfoSettingPaymentInfo.handleActivatePaymentInfo()' );

		let [ expiryMonth, expiryYear ] = split( card.expDate, '/' );
		expiryMonth = parseInt( trim( expiryMonth ) );
		expiryYear = parseInt( trim( expiryYear ) );

		const currentMonth = parseInt( moment().format( 'MM' ) );
		const currentYear = parseInt( moment().format( 'YY' ) );

		if ( expiryYear < currentYear || ( expiryYear === currentYear && expiryMonth < currentMonth ) ) {
			overlay.open( {
				settings: {
					modalType: OVERLAY_VIEWS.PAYMENT_CARD_ACTIVATION,
                    showCloseButton: false,
                    ignoreOutsideClick: false
				},
				data: {
					onActivateActionClick: this.submitActivatePaymentInfo,
					onSubmitEditedDate: this.handleEditPaymentInfo,
					onCancelClick: overlay.close,
                    onHsaFsaActionToggle: this.handleHsaFsaActionToggle,
					actions: this.state.componentData.modifyActions,
					requestPayload: this.props.requestPayload,
					card
				}
			} );
		} else {
			const action = find( this.state.componentData.modifyActions, { name: 'activate' } );
			this.submitActivatePaymentInfo( { url: action.url, id: card.id, month: toString( expiryMonth ), year: toString( expiryYear ) } );
		}
	}

	/**
	 * submit activate payment info
	 */
	async submitActivatePaymentInfo( { url, id, month, year } ) {
		log.debug( 'MemberInfoSettingPaymentInfo.submitActivatePaymentInfo()' );

		this.memberInfoSettingPaymentInfoService.cancel();

		overlay.close();

		pageLevelPreloader.show();

		const { status, data } = await this.memberInfoSettingPaymentInfoService.submitActivatePaymentInfoRequest( {
			url,
			data: {
				context: { memberId: this.props.requestPayload.context.memberId, patientId: this.props.requestPayload.context.patientId  },
				data: {
					id,
					month,
					year
				}
			}
		} );

		pageLevelPreloader.hide();

		if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
			const componentData = cloneDeep( this.state.componentData );

			const updatedList = map( componentData.paymentOptions, ( item ) => {
				if ( item.id === id ) {
					const updatedItem = cloneDeep( item );
					updatedItem.flags.isActivated = true;

					return updatedItem;
				}

				return item;
			} );
			
			componentData.paymentOptions = updatedList;

			this.setState( {
				componentData,
				networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
			} );
		} else {
			if( ! isEmpty( get( data, 'description' ) ) ) {
				errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
			} else {
				errorOverlay.open();
			}
		}
	}

    /**
     * update delete payment info
     */
     async handleInactivatePaymentInfo( { card } ) {
		log.debug( 'MemberInfoSettingPaymentInfo.handleInactivatePaymentInfo()' );

		const action = find( this.state.componentData.modifyActions, {
			name: 'inactivate',
		} );

		const shouldProceed = await confirmOverlay.open( {
			message: getLabel( action.confirmation.message ),
			iconName: 'close',
			confirmViewConfig: {
				type: 'paymentInfo',
				card: card
			}
		} );

		if ( shouldProceed ) {

			// cancel ongoing request
			this.memberInfoSettingPaymentInfoService.cancel();

			//hide preloader
			pageLevelPreloader.show();

			// set the address default
			const { status, data } = await this.memberInfoSettingPaymentInfoService.deletePaymentInfo( {
				url: action.url,
				data: {
					context: { memberId: this.props.requestPayload.context.memberId, patientId: this.props.requestPayload.context.patientId },
					data: { id: card.id }
				}
			} );

			//hide preloader
			pageLevelPreloader.hide();

			if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

				// update payment options
				const componentData = cloneDeep( this.state.componentData );

				const updatedList = map( componentData.paymentOptions, ( item ) => {
					if ( item.id === card.id ) {
						const updatedItem = cloneDeep( item );
						updatedItem.flags.isActivated = false;
						updatedItem.flags.isDefault = false;

						return updatedItem;
					}

					return item;
				} );
				
				componentData.paymentOptions = updatedList;

				this.setState( {
					componentData,
					networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
				} );
			} else {
				if( ! isEmpty( get( data, 'description' ) ) ) {
					errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
				} else {
					errorOverlay.open();
				}
			}
		}
	}

	/**
	 * submit hsaFsa toggle info
	 */
     async handleHsaFsaActionToggle( { url, id, flags }, callback = noop  ) {
		log.debug( 'MemberInfoSettingPaymentInfo.handleHsaFsaActionToggle()' );

		this.memberInfoSettingPaymentInfoService.cancel();

		// overlay.close();

		pageLevelPreloader.show();

		const { status, data } = await this.memberInfoSettingPaymentInfoService.submitToggleHsaFsaPaymentInfoRequest( {
			url,
			data: {
				context: { memberId: this.props.requestPayload.context.memberId, patientId: this.props.requestPayload.context.patientId },
				data: {
					id,
                    isHsaFsa: ! get( flags, 'isHsaFsa', false )
				}
			}
		} );

		pageLevelPreloader.hide();

		if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
			const componentData = cloneDeep( this.state.componentData );

			const updatedList = map( componentData.paymentOptions, ( item ) => {
				if ( item.id === id ) {
					const updatedItem = cloneDeep( item );
					updatedItem.flags.isHsaFsa = ! get( flags, 'isHsaFsa', false );

					return updatedItem;
				}

				return item;
			} );
			
			componentData.paymentOptions = updatedList;

			this.setState( {
				componentData,
				networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
			}, callback );
		} else {
			if( ! isEmpty( get( data, 'description' ) ) ) {
				errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
			} else {
				errorOverlay.open();
			}
		}
	}

    /*************************************************/

    /**
     * @desc Render a React view component
     */
    render() {

        // const memberId = get( this.props.location, 'state.memberId', undefined );

        // construct render redirection path for add payment
        const addPaymentRedirectPath = queryString.stringifyUrl( {
            url: this.props.location.pathname,
            query: {
                memberInfoSettings: 'memberInfoPaymentInfo', // active tab
            }
        } );

        return (
            <MemberInfoSettingPaymentInfoView
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                { ...get( this.state, 'componentData', {} )}
                requestPayload={ this.props.requestPayload }
                onPaymentOptionSelection={ this.handlePaymentOptionSelection }
                onPaymentDefaultSelection={ this.handlePaymentDefaultSelection }
                onPaymemntInfoEdit = { this.handleEditPaymentInfo }
                onPaymemntInfoActivate={ this.handleActivatePaymentInfo }
                onPaymemntInfoInactivate = { this.handleInactivatePaymentInfo }
                onHsaFsaActionToggle = { this.handleHsaFsaActionToggle }
                addPaymentRedirectPath={ addPaymentRedirectPath }
                showCustomErrorView={ this.state.showCustomErrorView }
            />
        );
    }
}

// set display name
_MemberInfoSettingPaymentInfo.displayName = '_MemberInfoSettingPaymentInfo';

// set default props
_MemberInfoSettingPaymentInfo.defaultProps = {};

// prop types
_MemberInfoSettingPaymentInfo.propTypes = {};

/*********************************************/

// with router functionality
export const MemberInfoSettingPaymentInfo = withRouter( _MemberInfoSettingPaymentInfo );

